import { useForm } from "@inertiajs/react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { RequestItem } from "@/types";

interface NewQuoteItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote_id: number;
  availableRequestItems: RequestItem[];
  matchedRequestItemIds: Set<number>;
}

const parseCommaNumber = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  if (!/^[0-9]+(,[0-9]+)?$/.test(raw)) return null;
  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

export default function NewQuoteItemModal({
  open,
  onOpenChange,
  quote_id,
  availableRequestItems,
  matchedRequestItemIds,
}: NewQuoteItemModalProps) {
  const routes = useOrganizationRoutes();
  const [requestItemSelectorOpen, setRequestItemSelectorOpen] = useState(false);
  const [qtyStr, setQtyStr] = useState("");
  const [priceStr, setPriceStr] = useState("");
  const [discountStr, setDiscountStr] = useState("");

  const { data, setData, post, processing, errors, reset } = useForm({
    quote_item: {
      quote_id: quote_id,
      request_item_id: null as number | null,
      supplier_product_name: "",
      quantity: 1,
      unit: "",
      unit_price: 0,
      discount_percentage: 0,
      item_type: "product",
      is_taxable: true,
    },
  });

  const handleRequestItemSelection = (requestItemId: number | null) => {
    if (requestItemId) {
      const selectedRequestItem = availableRequestItems.find(
        (item) => item.id === requestItemId,
      );
      if (selectedRequestItem) {
        setData("quote_item", {
          ...data.quote_item,
          request_item_id: requestItemId,
          supplier_product_name: selectedRequestItem.description,
          quantity: selectedRequestItem.quantity,
          unit: selectedRequestItem.unit,
        });
        setQtyStr(String(selectedRequestItem.quantity).replace(".", ","));
      }
    } else {
      setData("quote_item", {
        ...data.quote_item,
        request_item_id: null,
        supplier_product_name: "",
        quantity: 1,
        unit: "",
      });
      setQtyStr("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!data.quote_item.request_item_id) return;

    post(routes.quoteItems.index(), {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        setQtyStr("");
        setPriceStr("");
        setDiscountStr("");
        onOpenChange(false);
      },
    });
  };

  const handleClose = () => {
    reset();
    setQtyStr("");
    setPriceStr("");
    setDiscountStr("");
    onOpenChange(false);
  };

  const isQtyValid = parseCommaNumber(qtyStr) !== null;
  const isPriceValid = parseCommaNumber(priceStr) !== null;
  const isDiscountValid = discountStr.trim() === "" || parseCommaNumber(discountStr) !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Ítem</DialogTitle>
          <DialogDescription>
            Selecciona un ítem de la solicitud de compra y completa los datos del proveedor.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Request Item Selection */}
          <div className="space-y-2">
            <Label htmlFor="request-item">Ítem de Solicitud *</Label>
            <Popover
              open={requestItemSelectorOpen}
              onOpenChange={setRequestItemSelectorOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={requestItemSelectorOpen}
                  className="w-full justify-between font-normal"
                >
                  {data.quote_item.request_item_id ? (
                    (() => {
                      const item = availableRequestItems.find(
                        (item) => item.id === data.quote_item.request_item_id,
                      );
                      return item
                        ? `Item #${item.item_number} - ${item.description}`
                        : "Ítem no encontrado";
                    })()
                  ) : (
                    <span className="text-muted-foreground">
                      Selecciona un ítem de la solicitud...
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar ítem..." />
                  <CommandList>
                    <CommandEmpty>No se encontraron ítems.</CommandEmpty>
                    <CommandGroup>
                      {availableRequestItems.map((item) => {
                        const isAlreadyMatched = matchedRequestItemIds.has(item.id);
                        return (
                          <CommandItem
                            key={item.id}
                            value={`${item.item_number} ${item.description}`}
                            onSelect={() => {
                              if (!isAlreadyMatched) {
                                handleRequestItemSelection(item.id);
                                setRequestItemSelectorOpen(false);
                              }
                            }}
                            className={`flex flex-col items-start gap-1 p-3 ${
                              isAlreadyMatched
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            disabled={isAlreadyMatched}
                          >
                            <div className="flex w-full items-center gap-2">
                              <Check
                                className={`mr-2 h-4 w-4 ${data.quote_item.request_item_id === item.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium">
                                    Item #{item.item_number}
                                  </div>
                                  {isAlreadyMatched && (
                                    <Badge variant="secondary" className="text-xs">
                                      Ya asignado
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {item.description}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Cantidad: {item.quantity} {item.unit}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {((errors as unknown as { quote_item?: Record<string, string[]> }).quote_item)?.request_item_id && (
              <p className="text-xs text-red-500">
                Debe seleccionar un ítem de la solicitud
              </p>
            )}
          </div>

          {/* Quantity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="text"
                inputMode="decimal"
                value={qtyStr}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                    setQtyStr(raw);
                    const parsed = parseCommaNumber(raw);
                    setData("quote_item", {
                      ...data.quote_item,
                      quantity: parsed ?? data.quote_item.quantity,
                    });
                  }
                }}
                placeholder="Ej: 10 o 10,5"
                className={!isQtyValid && qtyStr ? "border-red-500" : ""}
              />
            </div>

            {/* Unit Price */}
            <div className="space-y-2">
              <Label htmlFor="unit_price">Precio Unitario *</Label>
              <Input
                id="unit_price"
                type="text"
                inputMode="decimal"
                value={priceStr}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                    setPriceStr(raw);
                    const parsed = parseCommaNumber(raw);
                    setData("quote_item", {
                      ...data.quote_item,
                      unit_price: parsed ?? data.quote_item.unit_price,
                    });
                  }
                }}
                placeholder="Ej: 1000 o 1000,50"
                className={!isPriceValid && priceStr ? "border-red-500" : ""}
              />
            </div>
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label htmlFor="discount">Descuento (%)</Label>
            <Input
              id="discount"
              type="text"
              inputMode="decimal"
              value={discountStr}
              onChange={(e) => {
                const raw = e.target.value;
                if (/^[0-9]*[,]?[0-9]*$/.test(raw)) {
                  setDiscountStr(raw);
                  const parsed = parseCommaNumber(raw);
                  setData("quote_item", {
                    ...data.quote_item,
                    discount_percentage: parsed ?? 0,
                  });
                }
              }}
              placeholder="Opcional: 10 o 10,5"
              className={!isDiscountValid && discountStr ? "border-red-500" : ""}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                processing ||
                !data.quote_item.request_item_id ||
                !isQtyValid ||
                !isPriceValid ||
                !isDiscountValid
              }
            >
              {processing ? "Guardando..." : "Guardar Ítem"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
